import { useEffect, useMemo, useRef, useState } from 'react';
import type { Loan, LoanEvent } from '@/types';
import { listEvents } from '@/lib/db';
import { todayIso, isBefore } from '@/lib/calc/dates';
import { generateReportPdf } from './reportPdf';
import { validateBackupFile, type BackupFile } from '@/lib/backupSerialize';
import { buildCurrentBackup } from '@/lib/currentBackup';
import { restoreBackup } from '@/lib/backupRestore';
import { requestDriveAccessToken, fetchSignedInEmail, verifyAuthorizedAccount, getAuthorizedAccountEmail, DriveAuthError } from '@/lib/driveAuth';
import {
  uploadBackupWithRetention,
  listDriveBackups,
  downloadAndValidateDriveBackup,
  type DriveBackupListItem,
  type RetentionResult,
} from '@/lib/driveBackup';
import {
  runAutoBackupIfDue,
  setDriveSessionToken,
  readLastAutoBackupAt,
  getBrowserStorage,
  type AutoBackupResult,
} from '@/lib/driveAutoBackup';
import { ReportsIcon, BackupRestoreIcon } from '@/components/icons';

interface RecordsPageProps {
  loan: Loan;
  /** Called after a successful restore, so the app re-fetches the (possibly entirely different) loan record — same pattern as SettingsPage's onLoanUpdated. */
  onRestored: () => void;
}

/**
 * Records — the combined Reports + Backup & Restore destination. This is
 * a NAVIGATION CONSOLIDATION ONLY: the two halves below are the former
 * ReportPage.tsx and BackupPage.tsx, unchanged in behaviour, placed
 * under one screen as two clearly separated sections (see
 * .records-section / .section-title) rather than being merged into one
 * function or one data model. Every handler, state machine, and guard
 * (wrong-account block, atomic restore, retention, auto-backup status)
 * is identical to before.
 */
export function RecordsPage({ loan, onRestored }: RecordsPageProps) {
  return (
    <div className="stack" style={{ gap: 'var(--space-7)' }}>
      <div>
        <h1>Records</h1>
        <p>Generate a formal report, and protect or recover the application's data.</p>
      </div>

      <ReportsSection loan={loan} />
      <BackupRestoreSection onRestored={onRestored} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reports (formerly ReportPage.tsx) — unchanged behaviour
// ---------------------------------------------------------------------------

function ReportsSection({ loan }: { loan: Loan }) {
  const [events, setEvents] = useState<LoanEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [asOfDate, setAsOfDate] = useState<string>(() => todayIso());
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    listEvents({ loanId: loan.id }).then((result) => {
      if (!cancelled) {
        setEvents(result);
        setIsLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [loan.id]);

  // Same earliest-valid-date bound as Home/Schedule's own "As of" selectors.
  const earliestValidDate = useMemo(() => {
    const firstDisbursementDate = events
      .filter((e) => e.type === 'disbursement' && !e.supersededAt)
      .map((e) => e.effectiveDate)
      .sort()[0];
    if (loan.loanStartDate && firstDisbursementDate) {
      return isBefore(firstDisbursementDate, loan.loanStartDate) ? firstDisbursementDate : loan.loanStartDate;
    }
    return loan.loanStartDate ?? firstDisbursementDate;
  }, [loan.loanStartDate, events]);

  const today = todayIso();
  const isFutureDate = asOfDate > today;

  function handleAsOfDateChange(next: string) {
    if (!next) return;
    if (earliestValidDate && next < earliestValidDate) {
      return;
    }
    setAsOfDate(next);
  }

  async function handleGenerate() {
    setError(undefined);
    setIsGenerating(true);
    try {
      const bytes = await generateReportPdf({ loan, events, asOfDate });
      const blob = new Blob([new Uint8Array(bytes).buffer], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `LIC-HFL-Loan-Report-${asOfDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setError('Something went wrong generating the PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }

  const hasSchedule = events.some((e) => e.type === 'disbursement');

  return (
    <section className="records-section">
      <div className="records-section-header">
        <span className="records-section-icon">
          <ReportsIcon size={20} />
        </span>
        <div>
          <div className="section-title">Reports</div>
          <p className="supporting-text" style={{ margin: 0 }}>
            Generate a formal PDF snapshot of this loan as of a selected date.
          </p>
        </div>
      </div>

      {isLoading && <p className="text-muted">Loading…</p>}

      {!isLoading && !hasSchedule && (
        <div className="empty-state">
          <div className="empty-state-icon">
            <ReportsIcon size={28} />
          </div>
          <p>Record at least one disbursement on the Timeline before generating a report.</p>
        </div>
      )}

      {!isLoading && hasSchedule && (
        <div className="card">
          <div className="field-group" style={{ marginBottom: 0 }}>
            <label htmlFor="reportAsOfDate">As of</label>
            <input
              id="reportAsOfDate"
              type="date"
              value={asOfDate}
              min={earliestValidDate}
              onChange={(e) => handleAsOfDateChange(e.target.value)}
            />
            {isFutureDate && (
              <span className="field-hint">
                You've selected a future date. The report will contain projected figures for that date, clearly
                marked as such.
              </span>
            )}
          </div>

          <button type="button" className="btn btn-block" style={{ marginTop: 16 }} onClick={handleGenerate} disabled={isGenerating}>
            {isGenerating ? 'Generating…' : 'Generate PDF Report'}
          </button>

          {error && (
            <p className="field-error" style={{ marginTop: 8 }}>
              {error}
            </p>
          )}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Backup & Restore (formerly BackupPage.tsx) — unchanged behaviour
// ---------------------------------------------------------------------------

/**
 * Sign in with Google and verify the account. Authenticating is
 * deliberately NOT the same as being authorized: 'authorized' is only
 * reached once the signed-in email matches the single configured
 * VITE_AUTHORIZED_GOOGLE_EMAIL — a successful Google sign-in with any
 * OTHER account lands in 'wrong_account', not 'authorized', and every
 * Drive action below is only reachable from 'authorized'. The access
 * token is held only in this component's state (never persisted).
 */
type DriveConnectState =
  | { stage: 'idle' }
  | { stage: 'connecting' }
  | { stage: 'authorized'; email: string; accessToken: string }
  | { stage: 'wrong_account'; email: string }
  | { stage: 'error'; message: string };

type DriveUploadState =
  | { stage: 'idle' }
  | { stage: 'uploading' }
  | { stage: 'success'; fileName: string; retention: RetentionResult }
  | { stage: 'error'; message: string };

/** Outcome of the automatic backup check for this session; 'idle' until Drive is connected and checked. */
type AutoBackupUiState = { stage: 'idle' } | { stage: 'running' } | { stage: 'done'; result: AutoBackupResult };

/** One short, non-alarming sentence describing what cleanup of older backups did. Undefined = nothing worth saying. */
function retentionNote(retention: RetentionResult): string | undefined {
  switch (retention.status) {
    case 'completed':
      return `Removed ${retention.deletedCount} older backup${retention.deletedCount === 1 ? '' : 's'} (keeping the newest 5).`;
    case 'partial':
      return `Your new backup is safe, but ${retention.failedCount} older backup${retention.failedCount === 1 ? '' : 's'} could not be removed.`;
    case 'failed':
      return 'Your new backup is safe, but cleaning up older backups did not complete.';
    case 'not_needed':
      return undefined;
  }
}

function autoBackupNote(state: AutoBackupUiState): string | undefined {
  if (state.stage === 'running') return 'Automatic backup: running…';
  if (state.stage !== 'done') return undefined;
  const r = state.result;
  switch (r.status) {
    case 'success':
      return `Automatic backup completed (${r.fileName}).`;
    case 'failed':
      return r.reason === 'auth_expired'
        ? 'Automatic backup failed: your Google session expired. Reconnect to try again.'
        : 'Automatic backup failed. Your data on this device is unaffected; it will be retried the next time you connect.';
    case 'skipped_no_storage':
      return 'Automatic backup is unavailable because this browser is blocking local storage.';
    case 'skipped_no_data':
    case 'skipped_not_due':
    case 'skipped_not_connected':
    case 'skipped_in_progress':
      return undefined; // nothing useful to add; the "last automatic backup" line below covers it
  }
}

/** Drive restore: list -> pick -> (download + validate) -> explicit confirm -> existing atomic restoreBackup(). */
type DriveRestoreState =
  | { stage: 'idle' }
  | { stage: 'listing' }
  | { stage: 'list'; items: DriveBackupListItem[] }
  | { stage: 'downloading'; fileName: string }
  | { stage: 'invalid'; errors: string[] }
  | { stage: 'confirming'; backup: BackupFile; fileName: string }
  | { stage: 'restoring'; backup: BackupFile }
  | { stage: 'success' }
  | { stage: 'error'; message: string };

type RestoreState =
  | { stage: 'idle' }
  | { stage: 'invalid'; errors: string[] }
  | { stage: 'confirming'; backup: BackupFile; fileName: string }
  | { stage: 'restoring'; backup: BackupFile }
  | { stage: 'error'; message: string };

/**
 * Backup & Restore — local export/import, plus manual Google Drive backup
 * and restore. Drive is backup/recovery storage only: nothing here ever
 * makes Drive required for local saves, and a Drive failure never
 * touches local data. This screen (section) is the one place in the app
 * that can replace the entire local dataset, so every restore — from a
 * file or from Drive — requires the user to see exactly what will
 * happen and explicitly confirm before anything is written.
 */
function BackupRestoreSection({ onRestored }: { onRestored: () => void }) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | undefined>();
  const [restoreState, setRestoreState] = useState<RestoreState>({ stage: 'idle' });
  const [driveConnectState, setDriveConnectState] = useState<DriveConnectState>({ stage: 'idle' });
  const [driveUploadState, setDriveUploadState] = useState<DriveUploadState>({ stage: 'idle' });
  const [driveRestoreState, setDriveRestoreState] = useState<DriveRestoreState>({ stage: 'idle' });
  const [autoBackupState, setAutoBackupState] = useState<AutoBackupUiState>({ stage: 'idle' });
  const [lastAutoBackupAt, setLastAutoBackupAt] = useState<string | undefined>(() => readLastAutoBackupAt(getBrowserStorage(), new Date()));
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * Fire-and-forget: never awaited by the connect flow, never throws
   * (runAutoBackupIfDue returns a status for every outcome), and only
   * READS local data — so it cannot delay or affect the UI or the
   * local database. At most one runs at a time (guarded in
   * driveAutoBackup.ts), and it is a no-op unless 24h have passed since
   * the last successful AUTOMATIC backup.
   */
  async function runAutomaticBackupCheck() {
    setAutoBackupState({ stage: 'running' });
    const result = await runAutoBackupIfDue({ buildBackup: buildCurrentBackup });
    setAutoBackupState({ stage: 'done', result });
    if (result.status === 'success') setLastAutoBackupAt(result.completedAt);
  }

  async function handleConnectDrive() {
    setDriveConnectState({ stage: 'connecting' });
    try {
      const { accessToken } = await requestDriveAccessToken();
      const email = await fetchSignedInEmail(accessToken);
      const isAuthorized = verifyAuthorizedAccount(email, getAuthorizedAccountEmail());
      if (!isAuthorized) {
        // A token from the wrong account must never be usable by anything else in the app.
        setDriveSessionToken(undefined);
        setDriveConnectState({ stage: 'wrong_account', email });
        return;
      }
      // Kept in memory only (never persisted) so the app-level automatic-backup check can see it this session.
      setDriveSessionToken(accessToken);
      setDriveConnectState({ stage: 'authorized', email, accessToken });
      void runAutomaticBackupCheck();
    } catch (err) {
      const message = err instanceof DriveAuthError ? err.message : 'Something went wrong signing in with Google.';
      setDriveConnectState({ stage: 'error', message });
    }
  }

  async function handleBackupToDrive() {
    if (driveConnectState.stage !== 'authorized') return;
    const { accessToken } = driveConnectState;
    setDriveUploadState({ stage: 'uploading' });
    try {
      // Local data is only READ here; a Drive failure can never modify it.
      const backup = await buildCurrentBackup();
      // Upload first; retention runs only after the upload is verified, and can never fail the backup itself.
      const { upload, retention } = await uploadBackupWithRetention(accessToken, backup);
      setDriveUploadState({ stage: 'success', fileName: upload.fileName, retention });
    } catch {
      setDriveUploadState({
        stage: 'error',
        message: 'Backup to Google Drive failed. Your data on this device is unaffected. Please try again.',
      });
    }
  }

  async function handleListDriveBackups() {
    if (driveConnectState.stage !== 'authorized') return;
    setDriveRestoreState({ stage: 'listing' });
    try {
      const items = await listDriveBackups(driveConnectState.accessToken);
      setDriveRestoreState({ stage: 'list', items });
    } catch {
      setDriveRestoreState({ stage: 'error', message: 'Could not list your Google Drive backups. Your data on this device is unaffected.' });
    }
  }

  async function handleSelectDriveBackup(item: DriveBackupListItem) {
    if (driveConnectState.stage !== 'authorized') return;
    setDriveRestoreState({ stage: 'downloading', fileName: item.fileName });
    try {
      const result = await downloadAndValidateDriveBackup(driveConnectState.accessToken, item.fileId);
      if (!result.valid) {
        setDriveRestoreState({ stage: 'invalid', errors: result.errors });
        return;
      }
      setDriveRestoreState({ stage: 'confirming', backup: result.backup, fileName: item.fileName });
    } catch {
      setDriveRestoreState({ stage: 'error', message: 'Could not download that backup from Google Drive. Your data on this device is unaffected.' });
    }
  }

  async function handleConfirmDriveRestore() {
    if (driveRestoreState.stage !== 'confirming') return;
    const { backup } = driveRestoreState;
    setDriveRestoreState({ stage: 'restoring', backup });
    try {
      await restoreBackup(backup);
      setDriveRestoreState({ stage: 'success' });
      onRestored();
    } catch {
      setDriveRestoreState({ stage: 'error', message: 'Restore failed. Your existing data has not been changed.' });
    }
  }

  function handleCancelDriveRestore() {
    setDriveRestoreState({ stage: 'idle' });
  }

  async function handleDownload() {
    setDownloadError(undefined);
    setIsDownloading(true);
    try {
      const backup = await buildCurrentBackup();
      const json = JSON.stringify(backup, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const datePart = backup.exportedAt.slice(0, 10);
      a.download = `LIC-HFL-Loan-Backup-${datePart}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setDownloadError('Something went wrong creating the backup. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  }

  function handleChooseFile() {
    fileInputRef.current?.click();
  }

  async function handleFileSelected(file: File | undefined) {
    if (!file) return;

    try {
      const text = await file.text();
      const parsed: unknown = JSON.parse(text);
      const result = validateBackupFile(parsed);
      if (!result.valid) {
        setRestoreState({ stage: 'invalid', errors: result.errors });
        return;
      }
      setRestoreState({ stage: 'confirming', backup: result.backup, fileName: file.name });
    } catch {
      setRestoreState({ stage: 'invalid', errors: ['This file could not be read as a backup. It may not be valid JSON.'] });
    }
  }

  async function handleConfirmRestore() {
    if (restoreState.stage !== 'confirming') return;
    const { backup } = restoreState;
    setRestoreState({ stage: 'restoring', backup });
    try {
      await restoreBackup(backup);
      setRestoreState({ stage: 'idle' });
      onRestored();
    } catch {
      setRestoreState({ stage: 'error', message: 'Restore failed. Your existing data has not been changed.' });
    }
  }

  function handleCancelRestore() {
    setRestoreState({ stage: 'idle' });
  }

  const eventCountOf = (backup: BackupFile) =>
    backup.events.disbursement.length +
    backup.events.rate_change.length +
    backup.events.prepayment.length +
    backup.events.balance_correction.length +
    backup.events.charge.length +
    backup.events.emi_date_change.length;

  return (
    <section className="records-section">
      <div className="records-section-header">
        <span className="records-section-icon">
          <BackupRestoreIcon size={20} />
        </span>
        <div>
          <div className="section-title">Backup &amp; Restore</div>
          <p className="supporting-text" style={{ margin: 0 }}>
            Protect and recover the application's data — locally or via Google Drive.
          </p>
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Download Backup</h3>
        <p className="text-muted">
          Saves your complete loan record and full event history — including edited/deleted entries kept for audit
          purposes — as a single JSON file.
        </p>
        <button type="button" className="btn btn-block" onClick={handleDownload} disabled={isDownloading}>
          {isDownloading ? 'Preparing…' : 'Download Backup'}
        </button>
        {downloadError && (
          <p className="field-error" style={{ marginTop: 8 }}>
            {downloadError}
          </p>
        )}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Restore from File</h3>
        <p className="text-muted">
          Restoring REPLACES all data currently on this device with the contents of the backup file. This cannot be
          undone.
        </p>

        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            handleFileSelected(file);
          }}
        />

        {restoreState.stage !== 'confirming' && restoreState.stage !== 'restoring' && (
          <button type="button" className="btn btn-block" onClick={handleChooseFile}>
            Choose Backup File…
          </button>
        )}

        {restoreState.stage === 'invalid' && (
          <div className="card" style={{ borderColor: 'var(--color-danger)', marginTop: 12 }}>
            <p style={{ color: 'var(--color-text)', marginTop: 0 }}>This file can't be restored:</p>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {restoreState.errors.map((err, i) => (
                <li key={i} className="field-error">
                  {err}
                </li>
              ))}
            </ul>
          </div>
        )}

        {restoreState.stage === 'confirming' && (
          <div className="card" style={{ borderColor: 'var(--color-danger)', marginTop: 12 }}>
            <p style={{ color: 'var(--color-text)', marginTop: 0 }}>
              <strong>{restoreState.fileName}</strong> was exported on{' '}
              {new Date(restoreState.backup.exportedAt).toLocaleString()} and contains{' '}
              {restoreState.backup.loan ? '1 loan record' : 'no loan record'} and {eventCountOf(restoreState.backup)}{' '}
              event record{eventCountOf(restoreState.backup) === 1 ? '' : 's'} (including any edited or deleted
              entries).
            </p>
            <p style={{ color: 'var(--color-text)' }}>
              Restoring will permanently replace the loan record and all current event history on this device with
              the contents of this file. This cannot be undone. Continue?
            </p>
            <div className="row">
              <button type="button" className="btn" onClick={handleCancelRestore}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={handleConfirmRestore}>
                Confirm Restore
              </button>
            </div>
          </div>
        )}

        {restoreState.stage === 'restoring' && (
          <div className="card" style={{ marginTop: 12 }}>
            <p style={{ color: 'var(--color-text)', margin: 0 }}>Restoring…</p>
          </div>
        )}

        {restoreState.stage === 'error' && (
          <p className="field-error" style={{ marginTop: 8 }}>
            {restoreState.message}
          </p>
        )}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Google Drive Backup</h3>
        <p className="text-muted">
          Back up to, or restore from, a dedicated folder in your Google Drive. This is a manual, optional extra —
          the app works fully without it.
        </p>

        {(driveConnectState.stage === 'idle' || driveConnectState.stage === 'connecting') && (
          <button type="button" className="btn btn-block" onClick={handleConnectDrive} disabled={driveConnectState.stage === 'connecting'}>
            {driveConnectState.stage === 'connecting' ? 'Connecting…' : 'Connect Google Drive'}
          </button>
        )}

        {driveConnectState.stage === 'authorized' && (
          <>
            <div className="card" style={{ marginTop: 12 }}>
              <p style={{ color: 'var(--color-text)', margin: 0 }}>
                ✓ Signed in as <strong>{driveConnectState.email}</strong> — this is the authorized account for Drive
                backup.
              </p>
            </div>

            <button
              type="button"
              className="btn btn-block"
              style={{ marginTop: 12 }}
              onClick={handleBackupToDrive}
              disabled={driveUploadState.stage === 'uploading'}
            >
              {driveUploadState.stage === 'uploading' ? 'Backing up…' : 'Backup to Google Drive'}
            </button>
            {driveUploadState.stage === 'success' && (
              <p className="text-muted" style={{ marginTop: 8 }}>
                ✓ Backed up to Google Drive as <strong>{driveUploadState.fileName}</strong>.
                {retentionNote(driveUploadState.retention) ? ` ${retentionNote(driveUploadState.retention)}` : ''}
              </p>
            )}
            {driveUploadState.stage === 'error' && (
              <p className="field-error" style={{ marginTop: 8 }}>
                {driveUploadState.message}
              </p>
            )}

            <p className="text-muted" style={{ marginTop: 8 }}>
              Automatic backup runs at most once every 24 hours, when you connect Google Drive.
              {lastAutoBackupAt ? ` Last automatic backup: ${new Date(lastAutoBackupAt).toLocaleString()}.` : ' No automatic backup yet.'}
              {autoBackupNote(autoBackupState) ? ` ${autoBackupNote(autoBackupState)}` : ''}
            </p>

            {(driveRestoreState.stage === 'idle' || driveRestoreState.stage === 'success' || driveRestoreState.stage === 'error') && (
              <button type="button" className="btn btn-block" style={{ marginTop: 12 }} onClick={handleListDriveBackups}>
                Restore from Google Drive…
              </button>
            )}
            {driveRestoreState.stage === 'success' && (
              <p className="text-muted" style={{ marginTop: 8 }}>
                ✓ Restore complete.
              </p>
            )}
            {driveRestoreState.stage === 'error' && (
              <p className="field-error" style={{ marginTop: 8 }}>
                {driveRestoreState.message}
              </p>
            )}

            {(driveRestoreState.stage === 'listing' || driveRestoreState.stage === 'downloading') && (
              <p className="text-muted" style={{ marginTop: 12 }}>
                {driveRestoreState.stage === 'listing' ? 'Looking for backups…' : `Downloading ${driveRestoreState.fileName}…`}
              </p>
            )}

            {driveRestoreState.stage === 'list' && (
              <div className="card" style={{ marginTop: 12 }}>
                {driveRestoreState.items.length === 0 ? (
                  <p style={{ color: 'var(--color-text)', margin: 0 }}>No backups found in Google Drive yet.</p>
                ) : (
                  <>
                    <p style={{ color: 'var(--color-text)', marginTop: 0 }}>Choose a backup to restore:</p>
                    <div className="stack">
                      {driveRestoreState.items.map((item) => (
                        <button key={item.fileId} type="button" className="btn btn-block" onClick={() => handleSelectDriveBackup(item)}>
                          {item.fileName}
                          {item.createdTime ? ` — ${new Date(item.createdTime).toLocaleString()}` : ''}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                <button type="button" className="btn" style={{ marginTop: 12 }} onClick={handleCancelDriveRestore}>
                  Cancel
                </button>
              </div>
            )}

            {driveRestoreState.stage === 'invalid' && (
              <div className="card" style={{ borderColor: 'var(--color-danger)', marginTop: 12 }}>
                <p style={{ color: 'var(--color-text)', marginTop: 0 }}>This backup can't be restored:</p>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {driveRestoreState.errors.map((err, i) => (
                    <li key={i} className="field-error">
                      {err}
                    </li>
                  ))}
                </ul>
                <button type="button" className="btn" style={{ marginTop: 12 }} onClick={handleCancelDriveRestore}>
                  Close
                </button>
              </div>
            )}

            {driveRestoreState.stage === 'confirming' && (
              <div className="card" style={{ borderColor: 'var(--color-danger)', marginTop: 12 }}>
                <p style={{ color: 'var(--color-text)', marginTop: 0 }}>
                  <strong>{driveRestoreState.fileName}</strong> was exported on{' '}
                  {new Date(driveRestoreState.backup.exportedAt).toLocaleString()} and contains{' '}
                  {driveRestoreState.backup.loan ? '1 loan record' : 'no loan record'} and {eventCountOf(driveRestoreState.backup)}{' '}
                  event record{eventCountOf(driveRestoreState.backup) === 1 ? '' : 's'} (including any edited or deleted
                  entries).
                </p>
                <p style={{ color: 'var(--color-text)' }}>
                  Restoring will permanently replace the loan record and all current event history on this device with
                  the contents of this backup. This cannot be undone. Continue?
                </p>
                <div className="row">
                  <button type="button" className="btn" onClick={handleCancelDriveRestore}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-danger" onClick={handleConfirmDriveRestore}>
                    Confirm Restore
                  </button>
                </div>
              </div>
            )}

            {driveRestoreState.stage === 'restoring' && (
              <div className="card" style={{ marginTop: 12 }}>
                <p style={{ color: 'var(--color-text)', margin: 0 }}>Restoring…</p>
              </div>
            )}
          </>
        )}

        {driveConnectState.stage === 'wrong_account' && (
          <div className="card" style={{ borderColor: 'var(--color-danger)', marginTop: 12 }}>
            <p style={{ color: 'var(--color-text)', marginTop: 0 }}>
              Signed in as <strong>{driveConnectState.email}</strong>, but this is not the Google account authorized
              for this app's backups. Drive backup will not proceed with this account.
            </p>
            <button type="button" className="btn" onClick={handleConnectDrive}>
              Try a Different Account
            </button>
          </div>
        )}

        {driveConnectState.stage === 'error' && (
          <p className="field-error" style={{ marginTop: 8 }}>
            {driveConnectState.message}
          </p>
        )}
      </div>
    </section>
  );
}
